/**
 * BloomPass — owns the half-resolution bloom intermediates and the three
 * sub-pipelines (bright extract, horizontal blur, vertical blur). The
 * present pass is responsible for additively combining the result back
 * over the feedback texture; bloom only outputs the blurred bright pixels.
 *
 * Texture lifecycle:
 *   - At construction we allocate two `rgba16float` half-res textures
 *     (`a`, `b`) sized to the surface's render dimensions / 2.
 *   - On every encode we render: bright -> a, blurH(a) -> b, blurV(b) -> a.
 *   - Callers read `getOutputView()` to bind the final result.
 *
 * Resize handling: `resize(width, height)` re-allocates the textures.
 */

import bloomWgsl from '../shaders/bloom.wgsl?raw';

export type BloomParams = {
  /** Brightness above which pixels start to bloom (in linear light). */
  threshold: number;
  /** Soft-knee width above the threshold. 0 = hard cliff. */
  knee: number;
  /** Stride multiplier for the blur taps; >1 widens the bloom. */
  spread: number;
};

const PARAMS_BYTES = 32;

export class BloomPass {
  private readonly device: GPUDevice;
  private readonly format: GPUTextureFormat = 'rgba16float';
  private readonly sampler: GPUSampler;
  private readonly paramsBuffer: GPUBuffer;
  private readonly cpuParams = new ArrayBuffer(PARAMS_BYTES);
  private readonly cpuParamsF32 = new Float32Array(this.cpuParams);

  private readonly bgl: GPUBindGroupLayout;
  private readonly brightPipeline: GPURenderPipeline;
  private readonly blurHPipeline: GPURenderPipeline;
  private readonly blurVPipeline: GPURenderPipeline;

  private texA!: GPUTexture;
  private texB!: GPUTexture;
  private viewA!: GPUTextureView;
  private viewB!: GPUTextureView;
  private width = 0;
  private height = 0;

  private currentParams: BloomParams = {
    threshold: 0.6,
    knee: 0.15,
    spread: 1.0,
  };

  constructor(device: GPUDevice, fullWidth: number, fullHeight: number) {
    this.device = device;

    this.sampler = device.createSampler({
      label: 'bloom-sampler',
      magFilter: 'linear',
      minFilter: 'linear',
      addressModeU: 'clamp-to-edge',
      addressModeV: 'clamp-to-edge',
    });

    this.paramsBuffer = device.createBuffer({
      label: 'bloom-params',
      size: PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const shader = device.createShaderModule({
      label: 'bloom-shader',
      code: bloomWgsl,
    });

    this.bgl = device.createBindGroupLayout({
      label: 'bloom-bgl',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.FRAGMENT,
          texture: { sampleType: 'float', viewDimension: '2d' },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.FRAGMENT,
          sampler: { type: 'filtering' },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
      ],
    });

    const layout = device.createPipelineLayout({ bindGroupLayouts: [this.bgl] });
    const targets = [{ format: this.format }];

    this.brightPipeline = device.createRenderPipeline({
      label: 'bloom-bright',
      layout,
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: { module: shader, entryPoint: 'fs_bright', targets },
      primitive: { topology: 'triangle-list' },
    });
    this.blurHPipeline = device.createRenderPipeline({
      label: 'bloom-blur-h',
      layout,
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: { module: shader, entryPoint: 'fs_blur_h', targets },
      primitive: { topology: 'triangle-list' },
    });
    this.blurVPipeline = device.createRenderPipeline({
      label: 'bloom-blur-v',
      layout,
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: { module: shader, entryPoint: 'fs_blur_v', targets },
      primitive: { topology: 'triangle-list' },
    });

    this.resize(fullWidth, fullHeight);
  }

  resize(fullWidth: number, fullHeight: number): void {
    const w = Math.max(2, Math.floor(fullWidth / 2));
    const h = Math.max(2, Math.floor(fullHeight / 2));
    if (w === this.width && h === this.height && this.texA) return;
    this.texA?.destroy();
    this.texB?.destroy();
    this.width = w;
    this.height = h;
    const usage = GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.TEXTURE_BINDING;
    this.texA = this.device.createTexture({
      label: 'bloom-A',
      size: { width: w, height: h },
      format: this.format,
      usage,
    });
    this.texB = this.device.createTexture({
      label: 'bloom-B',
      size: { width: w, height: h },
      format: this.format,
      usage,
    });
    this.viewA = this.texA.createView();
    this.viewB = this.texB.createView();
  }

  setParams(params: BloomParams): void {
    this.currentParams = params;
  }

  /** Output of the last `encode` call -- bind this in the present pass. */
  getOutputView(): GPUTextureView {
    return this.viewA;
  }

  encode(encoder: GPUCommandEncoder, sourceView: GPUTextureView): void {
    const f = this.cpuParamsF32;
    f[0] = this.currentParams.threshold;
    f[1] = this.currentParams.knee;
    f[2] = this.currentParams.spread;
    f[3] = 0;
    f[4] = 1.0 / this.width;
    f[5] = 1.0 / this.height;
    f[6] = 0;
    f[7] = 0;
    this.device.queue.writeBuffer(this.paramsBuffer, 0, this.cpuParams);

    const bgBright = this.makeBg(sourceView);
    const bgBlurH = this.makeBg(this.viewA);
    const bgBlurV = this.makeBg(this.viewB);

    this.runPass(encoder, this.brightPipeline, this.viewA, bgBright, 'bloom-bright');
    this.runPass(encoder, this.blurHPipeline, this.viewB, bgBlurH, 'bloom-blur-h');
    this.runPass(encoder, this.blurVPipeline, this.viewA, bgBlurV, 'bloom-blur-v');
  }

  private makeBg(view: GPUTextureView): GPUBindGroup {
    return this.device.createBindGroup({
      layout: this.bgl,
      entries: [
        { binding: 0, resource: view },
        { binding: 1, resource: this.sampler },
        { binding: 2, resource: { buffer: this.paramsBuffer } },
      ],
    });
  }

  private runPass(
    encoder: GPUCommandEncoder,
    pipeline: GPURenderPipeline,
    targetView: GPUTextureView,
    bg: GPUBindGroup,
    label: string,
  ): void {
    const pass = encoder.beginRenderPass({
      label,
      colorAttachments: [
        {
          view: targetView,
          loadOp: 'clear',
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, bg);
    pass.draw(6, 1, 0, 0);
    pass.end();
  }

  destroy(): void {
    this.texA?.destroy();
    this.texB?.destroy();
  }
}
